/* eslint-disable camelcase */
/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { GenericDataType } from './QueryResponse';
import { QueryFormColumn } from './QueryFormData';

export interface AdhocColumn {
  hasCustomLabel?: boolean;
  label?: string;
  optionName?: string;
  sqlExpression: string;
  expressionType: 'SQL';
  columnType?: 'BASE_AXIS' | 'SERIES';
  timeGrain?: string;
  datasourceWarning?: boolean;
}

/**
 * A column that is physically defined in datasource.
 */
export type PhysicalColumn = string;

/**
 * Metadata information for an enum value defined in Metadata Service schema.
 * This is used to provide additional context about the enum value,
 * such as its description, privacy level, and whether it is a tier zero column.
 *
 * Individual enumerated values can be owned by different teams and have different
 * privacy levels, which is why this metadata is defined at the enum value level.
 * e.g. for Action_Scenario you might have a CallStart enum value and a MessageSent enum value
 *
 * * @property {string} [description] - A description of the enum value.
 * * @property {string} [privacyLevel] - The privacy level of the enum value.
 * * @property {boolean} [isTierZero] - Indicates if the enum value is a tier zero column.
 */
export type EnumMetadata = {
  description?: string;
  privacyLevel?: string;
  isTierZero?: boolean;
};

/**
 * Metadata information for a column defined in Metadata Service.
 * This is used to provide additional context about the column,
 * such as its name, description, area path, aliases, and whether it is a core
 * or enum column. It may also include enum values if the column is an enum.
 * * @property {string} [name] - The name of the column.
 * * @property {string} [description] - A description of the column.
 * * @property {string} [areaPath] - The ADO area path associated with the column.
 * * @property {string[]} [aliases] - An array of aliases for the column.
 * * @property {string} [column_type] - The type of the column.
 * * @property {boolean} [isCore] - Indicates if the column is a core column.
 * * @property {boolean} [isEnum] - Indicates if the column is an enum column (e.g. it has a list of well defined values).
 * * @property {Object} [enumValues] - An object containing enum values if the column is an enum.
 * * @property {boolean} [isTierZero] - Indicates if the column is a tier zero column.
 */
export type MetadataServiceInfo = {
  name?: string;
  description?: string;
  areaPath?: string;
  aliases?: string[];
  column_type?: string;
  isCore?: boolean;
  isEnum?: boolean;
  enumValues?: { [value: string]: EnumMetadata };
  isTierZero?: boolean;
};

/**
 * Column information defined in datasource.
 */
export interface Column {
  id?: number;
  type?: string;
  type_generic?: GenericDataType;
  column_name: string;
  groupby?: boolean;
  is_dttm?: boolean;
  filterable?: boolean;
  verbose_name?: string | null;
  description?: string | null;
  expression?: string | null;
  database_expression?: string | null;
  python_date_format?: string | null;
  medatadata_service_info?: MetadataServiceInfo;

  // used for advanced_data_type
  optionName?: string;
  filterBy?: string;
  value?: string;
  advanced_data_type?: string;
}

export function isPhysicalColumn(column?: any): column is PhysicalColumn {
  return typeof column === 'string';
}

export function isAdhocColumn(column?: any): column is AdhocColumn {
  return (
    typeof column !== 'string' &&
    column?.sqlExpression !== undefined &&
    column?.label !== undefined &&
    (column?.expressionType === undefined || column?.expressionType === 'SQL')
  );
}

export function isQueryFormColumn(column: any): column is QueryFormColumn {
  return isPhysicalColumn(column) || isAdhocColumn(column);
}

export default {};
